/**
 * CommandPalette — quick-search modal that opens from the top-bar
 * search icon or via Cmd/Ctrl+K anywhere in the wallet. Fuzzy-matches
 * against every navigation destination + a small set of common
 * actions. Keyboard-first: ↑↓ to move, ⏎ to run, Esc to close.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, X, ArrowRight, Home, Wallet, FolderOpen, Building2,
  UserSquare2, ShieldCheck, Compass, LineChart, ClipboardList,
  UserCog, Info, Settings as SettingsIcon, Sparkles, AlertTriangle,
  FileText,
} from "lucide-react";
import { NAVY, GOLD } from "@/components/wallet/WalletShell";

const COMMANDS = [
  // Primary destinations
  { key: "home",         label: "Home",                  icon: Home,          to: "/wallet",              tags: ["dashboard","start"] },
  { key: "profile",      label: "Financial Profile",     icon: UserCog,       to: "/wallet/profile",      tags: ["income","employment","age","questions"] },
  { key: "documents",    label: "My Financial Documents", icon: FolderOpen,    to: "/wallet/documents",    tags: ["vault","upload","pdf","files","records","policy","insurance"] },
  { key: "institutions", label: "My Trusted Institutions", icon: Building2,   to: "/wallet/institutions", tags: ["banks","insurers","providers"] },
  { key: "record",       label: "My Consumer Record",    icon: UserSquare2,   to: "/wallet/record",       tags: ["decisions","history","audit","timeline"] },
  { key: "safety",       label: "Trust & Safety",        icon: ShieldCheck,   to: "/wallet/safety",       tags: ["scam","phishing","fraud","verify","report","impersonation","trust check"] },
  { key: "journeys",     label: "Life Journeys",         icon: Compass,       to: "/wallet/life-journeys", tags: ["retirement","first home","business","grief"] },
  { key: "investments",  label: "Investments",           icon: LineChart,     to: "/wallet/investments",  tags: ["portfolio","returns","market"] },
  { key: "assistance",   label: "My Assistance Requests", icon: ClipboardList, to: "/wallet/assistance",  tags: ["help","complaint","support"] },
  { key: "about",        label: "About",                 icon: Info,          to: "/wallet/about",        tags: ["what is","fair conduct","tcf","cofi","fsca"] },
  { key: "settings",     label: "Profile & settings",    icon: SettingsIcon,  to: "/wallet/settings",     tags: ["language","accessibility","account"] },
  // Actions
  { key: "ask-navi",     label: "Ask NAVI",              icon: Sparkles,      run: "navi.open",           tags: ["question","help","explain"] },
  { key: "report-scam",  label: "Report a suspicious message", icon: AlertTriangle, to: "/wallet/safety", tags: ["phishing","fraud","sms"] },
  { key: "analyse-doc",  label: "Analyse a document",    icon: FileText,      to: "/wallet/documents",    tags: ["pdf","policy","quote"] },
];

function scoreQuery(cmd, q) {
  if (!q) return 0;
  const hay = `${cmd.label} ${(cmd.tags || []).join(" ")}`.toLowerCase();
  if (hay.includes(q)) return 3;
  // simple char-in-order match
  let i = 0;
  for (const ch of hay) { if (ch === q[i]) i++; if (i === q.length) return 1; }
  return 0;
}

export default function CommandPalette({ open, onClose }) {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return COMMANDS.slice(0, 8);
    return COMMANDS
      .map(c => ({ c, s: scoreQuery(c, query) }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map(x => x.c)
      .slice(0, 8);
  }, [q]);

  useEffect(() => { setIdx(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    setQ(""); setIdx(0);
    setTimeout(() => inputRef.current?.focus(), 40);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setIdx(i => Math.min(results.length - 1, i + 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setIdx(i => Math.max(0, i - 1)); return; }
      if (e.key === "Enter")     { e.preventDefault(); runCommand(results[idx]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, results, idx, onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const runCommand = (c) => {
    if (!c) return;
    onClose();
    if (c.run === "navi.open") { window.dispatchEvent(new CustomEvent("navi.open")); return; }
    if (c.to) nav(c.to);
  };

  if (!open) return null;
  return (
    <div
      data-testid="command-palette"
      className="fixed inset-0 z-[80] flex items-start justify-center pt-24 px-4"
      style={{ background: "rgba(6,22,51,0.60)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search the wallet…"
            data-testid="command-palette-input"
            className="flex-1 text-[14px] focus:outline-none"
          />
          <kbd className="text-[10px] text-slate-400 font-mono">Esc</kbd>
          <button onClick={onClose} aria-label="Close" data-testid="command-palette-close">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {results.length === 0 ? (
            <div className="p-6 text-center text-[13px] text-slate-500">No matches for "{q}"</div>
          ) : (
            results.map((c, i) => (
              <button
                key={c.key}
                onClick={() => runCommand(c)}
                onMouseEnter={() => setIdx(i)}
                data-testid={`command-item-${c.key}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition"
                style={{ background: i === idx ? `${GOLD}22` : "transparent" }}
              >
                <c.icon className="w-4 h-4 flex-shrink-0" style={{ color: NAVY }} />
                <span className="flex-1 text-[13.5px] font-medium" style={{ color: NAVY }}>{c.label}</span>
                {i === idx && <ArrowRight className="w-3.5 h-3.5 text-slate-400" />}
              </button>
            ))
          )}
        </div>
        <div className="px-4 py-2 border-t border-slate-100 text-[10.5px] text-slate-400 flex items-center gap-3">
          <span><kbd className="font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono">⏎</kbd> open</span>
          <span><kbd className="font-mono">Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
